import React, { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  message
} from 'antd';
import { MinusCircleOutlined, MinusOutlined, PlusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPlanFeatures } from '../permissions/permissionManageData';
import {
  CURRENCY_OPTIONS,
  FEATURE_TYPE_OPTIONS,
  REGION_OPTIONS
} from '../packages/packageFormOptions';
import {
  createEntitlementPackage,
  getEntitlementPackageDetail,
  updateEntitlementPackage,
  type CreateEntitlementPackageBody,
  type CreateEntitlementPrice,
  type CreateEntitlementSpecification,
  type EntitlementFeatureDescription,
  type EntitlementSpecificationDetail
} from './benefitManageData';

const FEATURE_NO_PATTERN = /^[0-9]{21}$/;
const REGION_CODE_PATTERN = /^[A-Z]{2}$/;
const CURRENCY_CODE_PATTERN = /^[A-Z]{3}$/;
const QUOTA_PERIODS = ['DAY', 'WEEK', 'MONTH', 'TOTAL', 'NONE'] as const;
const QUOTA_PERIOD_OPTIONS = QUOTA_PERIODS.map((value) => ({ value, label: value }));
const CURRENCY_FRACTION_DIGITS: Record<string, number> = {
  CNY: 2,
  USD: 2,
  THB: 2,
  IDR: 2,
  MYR: 2,
  PHP: 2,
  SGD: 2,
  VND: 0
};

type FeatureFormItem = {
  displayName?: string;
  type?: 'QUOTA' | 'TEXT' | 'BOOL';
  limitValue?: number;
  unit?: string;
  period?: string;
  text?: string;
  allowed?: boolean;
  sort?: number;
};

type SpecPriceFormItem = {
  price?: number;
  currency?: string;
  region?: string;
};

type SpecFormItem = {
  validityDays?: number | null;
  quotaLimit?: number;
  prices: SpecPriceFormItem[];
};

type BenefitFormValues = {
  name: string;
  featureId?: string;
  quotaUnit?: string;
  features: FeatureFormItem[];
  specifications: SpecFormItem[];
};

function toAmountMinor(price: number, currency: string) {
  const digits = CURRENCY_FRACTION_DIGITS[currency] ?? 2;
  const minor = Math.round(price * 10 ** digits);
  if (!Number.isSafeInteger(minor)) return null;
  return minor;
}

function fromAmountMinor(amountMinor: number, currency: string) {
  const digits = CURRENCY_FRACTION_DIGITS[currency] ?? 2;
  return amountMinor / 10 ** digits;
}

function normalizeQuotaPeriod(raw?: string) {
  const text = raw?.trim() ?? '';
  if (!text) return '';
  const upper = text.toUpperCase();
  if ((QUOTA_PERIODS as readonly string[]).includes(upper)) return upper;
  const alias: Record<string, string> = {
    日: 'DAY',
    天: 'DAY',
    周: 'WEEK',
    月: 'MONTH',
    总次数: 'TOTAL',
    累计: 'TOTAL',
    持续容量: 'NONE',
    持续: 'NONE'
  };
  return alias[text] ?? '';
}

function mapFeatureDescriptions(list?: EntitlementFeatureDescription[]): FeatureFormItem[] {
  if (!list?.length) return [];
  return list.map((item, index) => {
    const displayType =
      item.displayType === 'QUOTA' || item.displayType === 'TEXT' ? item.displayType : 'BOOL';
    return {
      displayName: item.title?.trim() ?? '',
      type: displayType,
      limitValue: item.quota,
      unit: item.unit?.trim() ?? '',
      period: item.period?.trim() ?? '',
      text: item.text?.trim() ?? '',
      allowed: item.supported !== false,
      sort: index + 1
    };
  });
}

function emptyPrice(): SpecPriceFormItem {
  return { price: 0, currency: 'CNY', region: 'CN' };
}

function mapPrices(list?: EntitlementSpecificationDetail['prices']): SpecPriceFormItem[] {
  if (!list?.length) return [emptyPrice()];
  return list.map((item) => {
    const currency = item.currency?.trim().toUpperCase() || 'CNY';
    return {
      price: item.amountMinor == null ? 0 : fromAmountMinor(item.amountMinor, currency),
      currency,
      region: item.regionCode?.trim().toUpperCase() || 'CN'
    };
  });
}

function mapQuotaLimit(spec: EntitlementSpecificationDetail) {
  if (typeof spec.quotaLimit === 'number' && Number.isInteger(spec.quotaLimit) && spec.quotaLimit >= 1) {
    return spec.quotaLimit;
  }
  const fromPolicy = (spec.featurePolicies ?? []).find(
    (item) => typeof item.quotaLimit === 'number' && item.quotaLimit >= 1
  );
  return fromPolicy?.quotaLimit;
}

function buildFeatureDescriptions(
  features: FeatureFormItem[] | undefined
): EntitlementFeatureDescription[] | null {
  const ordered = (features ?? [])
    .map((item, index) => ({ item, index }))
    .sort(
      (a, b) => (a.item.sort ?? a.index + 1) - (b.item.sort ?? b.index + 1) || a.index - b.index
    );
  const result: EntitlementFeatureDescription[] = [];
  for (const { item } of ordered) {
    const title = item.displayName?.trim() ?? '';
    const displayType = item.type;
    if (!displayType || !['BOOL', 'QUOTA', 'TEXT'].includes(displayType)) {
      message.error('请选择功能展示类型 BOOL / QUOTA / TEXT');
      return null;
    }
    if (!title) {
      message.error('请输入功能展示名称');
      return null;
    }
    if (displayType === 'QUOTA') {
      const period = normalizeQuotaPeriod(item.period);
      const unit = item.unit?.trim() ?? '';
      const quota = item.limitValue ?? 0;
      if (
        !unit ||
        !Number.isInteger(quota) ||
        quota < 0 ||
        !(QUOTA_PERIODS as readonly string[]).includes(period)
      ) {
        message.error('QUOTA 需填写非负额度、单位，以及 DAY、WEEK、MONTH、TOTAL 或 NONE');
        return null;
      }
      result.push({ title, displayType, quota, unit, period });
      continue;
    }
    if (displayType === 'TEXT') {
      const text = item.text?.trim() ?? '';
      if (!text) {
        message.error('请输入文本说明');
        return null;
      }
      result.push({ title, displayType, text });
      continue;
    }
    if (item.allowed !== true && item.allowed !== false) {
      message.error('BOOL 类型须选择是否支持');
      return null;
    }
    result.push({ title, displayType: 'BOOL', supported: item.allowed });
  }
  return result;
}

function buildPrices(spec: SpecFormItem): CreateEntitlementPrice[] | null {
  const rows = spec.prices ?? [];
  if (rows.length === 0) {
    message.error('每个规格至少添加一项价格');
    return null;
  }
  const result: CreateEntitlementPrice[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const regionCode = row.region?.trim().toUpperCase() ?? '';
    const currency = row.currency?.trim().toUpperCase() ?? '';
    if (!REGION_CODE_PATTERN.test(regionCode) || !CURRENCY_CODE_PATTERN.test(currency)) {
      message.error('请选择地区和币种');
      return null;
    }
    const key = `${regionCode}:${currency}`;
    if (seen.has(key)) {
      message.error('同一规格的地区和币种组合不能重复');
      return null;
    }
    seen.add(key);
    const price: CreateEntitlementPrice = { regionCode, currency };
    if (row.price != null && row.price !== 0) {
      const amountMinor = toAmountMinor(row.price, currency);
      if (amountMinor == null || amountMinor < 1) {
        message.error('价格须为大于 0 的金额');
        return null;
      }
      price.amountMinor = amountMinor;
    }
    result.push(price);
  }
  return result;
}

function emptySpec(): SpecFormItem {
  return {
    quotaLimit: 1,
    prices: [emptyPrice()]
  };
}

function RowActions({
  onAdd,
  onRemove,
  removeDisabled,
  plain
}: {
  onAdd: () => void;
  onRemove: () => void;
  removeDisabled?: boolean;
  plain?: boolean;
}) {
  const buttonType = plain ? 'text' : 'primary';
  return (
    <Space size={8} className='admin-pkg-form-row-actions'>
      <Button
        type={buttonType}
        size='small'
        icon={<PlusCircleOutlined />}
        onClick={onAdd}
        style={plain ? { color: '#1677ff' } : undefined}
      />
      <Button
        type={buttonType}
        size='small'
        icon={<MinusCircleOutlined />}
        disabled={removeDisabled}
        onClick={onRemove}
        style={plain ? { color: '#f33e3e' } : undefined}
      />
    </Space>
  );
}

function FloatField({
  label,
  className,
  children
}: {
  label: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`admin-pkg-float-field${className ? ` ${className}` : ''}`}>
      <span className='admin-pkg-float-label'>{label}</span>
      {children}
    </div>
  );
}

function FeatureTypeFields({ name }: { name: number }) {
  const type = Form.useWatch(['features', name, 'type']) as string | undefined;

  if (type === 'QUOTA') {
    return (
      <>
        <FloatField label='额度' className='admin-pkg-form-ctrl admin-pkg-form-ctrl--sm'>
          <Form.Item name={[name, 'limitValue']} noStyle>
            <InputNumber min={0} precision={0} style={{ width: '100%' }} placeholder='0' />
          </Form.Item>
        </FloatField>
        <Form.Item name={[name, 'unit']} className='admin-pkg-form-ctrl admin-pkg-form-ctrl--sm'>
          <Input placeholder='单位' />
        </Form.Item>
        <Form.Item name={[name, 'period']} className='admin-pkg-form-ctrl admin-pkg-form-ctrl--sm'>
          <Select options={QUOTA_PERIOD_OPTIONS} placeholder='周期' />
        </Form.Item>
      </>
    );
  }

  if (type === 'TEXT') {
    return (
      <Form.Item name={[name, 'text']} className='admin-pkg-form-ctrl admin-pkg-form-ctrl--text'>
        <Input placeholder='文本，如不限次数' />
      </Form.Item>
    );
  }

  return (
    <Form.Item name={[name, 'allowed']} className='admin-pkg-form-ctrl admin-pkg-form-ctrl--radio'>
      <Radio.Group>
        <Radio value={true}>支持</Radio>
        <Radio value={false}>不支持</Radio>
      </Radio.Group>
    </Form.Item>
  );
}

export function BenefitForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const [form] = Form.useForm<BenefitFormValues>();
  const [featureOptions, setFeatureOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [featureLoading, setFeatureLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configHash, setConfigHash] = useState<string | undefined>();

  useEffect(() => {
    if (!editId) {
      setConfigHash(undefined);
      form.setFieldsValue({
        name: '',
        featureId: undefined,
        quotaUnit: '次',
        features: [],
        specifications: [emptySpec()]
      });
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    getEntitlementPackageDetail(editId)
      .then((res) => {
        if (cancelled) return;
        const detail = res.content?.data;
        if (res.code !== 200 || !detail) {
          if (res.code !== -1) message.error(res.message || '权益包详情加载失败');
          setConfigHash(undefined);
          return;
        }
        const hash = res.content?.configHash;
        setConfigHash(typeof hash === 'string' ? hash : undefined);
        const specs = detail.specifications ?? [];
        const featureCode = typeof detail.featureCode === 'string' ? detail.featureCode.trim() : '';
        if (FEATURE_NO_PATTERN.test(featureCode)) {
          setFeatureOptions((prev) =>
            prev.some((item) => item.value === featureCode)
              ? prev
              : [...prev, { value: featureCode, label: featureCode }]
          );
        }
        const packageFeatures =
          detail.featureDescriptions ??
          specs.find((spec) => (spec.featureDescriptions?.length ?? 0) > 0)?.featureDescriptions;
        const quotaUnit =
          detail.quotaUnit?.trim() ||
          packageFeatures?.find((item) => item.displayType === 'QUOTA' && item.unit?.trim())?.unit?.trim() ||
          '次';
        form.setFieldsValue({
          name: detail.displayName ?? '',
          featureId: FEATURE_NO_PATTERN.test(featureCode) ? featureCode : undefined,
          quotaUnit,
          features: mapFeatureDescriptions(packageFeatures),
          specifications: specs.length
            ? specs.map((spec) => ({
              validityDays:
                spec.validityDays != null && spec.validityDays >= 1 ? spec.validityDays : undefined,
              quotaLimit: mapQuotaLimit(spec),
              prices: mapPrices(spec.prices)
            }))
            : [emptySpec()]
        });
      })
      .catch(() => {
        if (!cancelled) message.error('权益包详情加载失败');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editId, form]);

  useEffect(() => {
    let cancelled = false;
    const loadFeatures = async () => {
      setFeatureLoading(true);
      const collected: Array<{ value: string; label: string }> = [];
      for (let pageNum = 1; pageNum <= 10; pageNum += 1) {
        const res = await getPlanFeatures({ pageNum, pageSize: 100 });
        if (cancelled) return;
        const records = res.content?.records;
        if (res.code !== 200 || !Array.isArray(records)) {
          if (res.code !== -1) message.error(res.message || '权限点加载失败');
          break;
        }
        records.forEach((item) => {
          const featureNo = item.featureNo?.trim() ?? '';
          if (!FEATURE_NO_PATTERN.test(featureNo)) return;
          collected.push({
            value: featureNo,
            label: item.displayName?.trim() || item.featureCode?.trim() || featureNo
          });
        });
        if (records.length < 100) break;
      }
      if (!cancelled) {
        setFeatureOptions((prev) => {
          const known = new Set(collected.map((item) => item.value));
          return [...collected, ...prev.filter((item) => !known.has(item.value))];
        });
      }
    };
    loadFeatures()
      .catch(() => {
        if (!cancelled) message.error('权限点加载失败');
      })
      .finally(() => {
        if (!cancelled) setFeatureLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onFinish = async (values: BenefitFormValues) => {
    if (saving || detailLoading) return;
    const displayName = values.name?.trim() ?? '';
    if (!displayName || displayName.length > 128) {
      message.error('权益名称必填，最多 128 个字符');
      return;
    }
    const featureCode = values.featureId?.trim() ?? '';
    if (!FEATURE_NO_PATTERN.test(featureCode)) {
      message.error('请选择权限点 featureCode');
      return;
    }
    const quotaUnit = values.quotaUnit?.trim() ?? '';
    if (!quotaUnit || quotaUnit.length > 32) {
      message.error('展示单位 quotaUnit 必填，最多 32 个字符');
      return;
    }
    if (editId && (typeof configHash !== 'string' || !configHash)) {
      message.warning('缺少配置校验值 expectedConfigHash，请关闭后重新打开');
      return;
    }

    const featureDescriptions = buildFeatureDescriptions(values.features);
    if (!featureDescriptions) return;

    const specifications: CreateEntitlementSpecification[] = [];
    for (const spec of values.specifications ?? []) {
      const quotaLimit = spec.quotaLimit;
      if (quotaLimit == null || !Number.isInteger(quotaLimit) || quotaLimit < 1) {
        message.error('规格总次数 quotaLimit 须为正整数');
        return;
      }
      const prices = buildPrices(spec);
      if (!prices) return;
      const specification: CreateEntitlementSpecification = { quotaLimit, prices };
      if (spec.validityDays != null) {
        if (!Number.isInteger(spec.validityDays) || spec.validityDays < 1) {
          message.error('有效天数须为不少于 1 的整数，留空表示永久');
          return;
        }
        specification.validityDays = spec.validityDays;
      }
      specifications.push(specification);
    }
    if (specifications.length === 0) {
      message.error('请至少添加一条规格');
      return;
    }

    const body: CreateEntitlementPackageBody = {
      displayName,
      featureDescriptions,
      featureCode,
      quotaUnit,
      specifications
    };

    setSaving(true);
    const res = editId
      ? await updateEntitlementPackage(editId, { ...body, expectedConfigHash: configHash as string })
      : await createEntitlementPackage(body);
    setSaving(false);
    if (res.code !== 200) {
      if (res.code !== -1) message.error(res.message || (editId ? '权益包保存失败' : '权益包创建失败'));
      return;
    }
    message.success(res.message || (editId ? '权益包已保存' : '权益包已创建'));
    navigate('/devops/creator-outreach/benefits');
  };

  return (
    <div className='admin-page-card admin-pkg-form-page'>
      <div className='admin-pkg-form-header'>
        <h2 className='admin-pkg-form-title'>{editId ? '编辑单独权益' : '新增单独权益'}</h2>
        <Space>
          <Button onClick={() => navigate('/devops/creator-outreach/benefits')}>取消</Button>
          <Button
            type='primary'
            className='admin-pkg-create-btn'
            loading={saving}
            disabled={detailLoading}
            onClick={() => form.submit()}
          >
            保存
          </Button>
        </Space>
      </div>

      <Form<BenefitFormValues>
        form={form}
        layout='horizontal'
        labelCol={{ flex: '110px' }}
        wrapperCol={{ flex: 1 }}
        labelAlign='right'
        requiredMark
        className='admin-pkg-form'
        onFinish={onFinish}
      >
        <Form.Item
          name='name'
          label='权益名称'
          rules={[{ required: true, message: '请输入权益名称' }]}
        >
          <Input placeholder='如：店铺槽位1个' maxLength={128} />
        </Form.Item>

        <Form.Item
          name='featureId'
          label='权益功能点'
          rules={[{ required: true, message: '请选择权益功能点' }]}
        >
          <Select
            options={featureOptions}
            placeholder='请选择权限点 featureCode'
            allowClear
            loading={featureLoading}
            showSearch
            optionFilterProp='label'
          />
        </Form.Item>

        <Form.Item
          name='quotaUnit'
          label='展示单位'
          rules={[
            { required: true, message: '请输入展示单位' },
            { max: 32, message: '最多 32 个字符' }
          ]}
        >
          <Input placeholder='如：次、封、个' maxLength={32} />
        </Form.Item>

        <Form.List name='features'>
          {(fields, { add, remove }) => (
            <Form.Item label='业务展示文案' className='admin-pkg-form-block'>
              <div className='admin-pkg-form-list'>
                {fields.length === 0 ? (
                  <div className='admin-benefit-feature-empty-row'>
                    <p className='admin-benefit-feature-empty'>可传空列表，所有规格共用</p>
                    <Button
                      type='link'
                      size='small'
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          type: 'QUOTA',
                          limitValue: 0,
                          unit: '',
                          period: 'TOTAL',
                          allowed: true,
                          sort: 1
                        })
                      }
                    >
                      添加
                    </Button>
                  </div>
                ) : (
                  fields.map((field) => (
                    <div key={field.key} className='admin-pkg-form-feature-row'>
                      <Form.Item
                        name={[field.name, 'displayName']}
                        className='admin-pkg-form-ctrl admin-pkg-form-ctrl--name'
                        rules={[{ required: true, message: '请输入' }]}
                      >
                        <Input placeholder='展示名称' />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'type']}
                        className='admin-pkg-form-ctrl admin-pkg-form-ctrl--type'
                        rules={[{ required: true, message: '请选择' }]}
                      >
                        <Select options={FEATURE_TYPE_OPTIONS} placeholder='类型' />
                      </Form.Item>
                      <FeatureTypeFields name={field.name} />
                      <FloatField label='排序' className='admin-pkg-form-ctrl admin-pkg-form-ctrl--sort'>
                        <Form.Item name={[field.name, 'sort']} noStyle>
                          <InputNumber min={1} precision={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </FloatField>
                      <RowActions
                        onAdd={() =>
                          add({
                            type: 'BOOL',
                            allowed: true,
                            sort: fields.length + 1
                          })
                        }
                        onRemove={() => remove(field.name)}
                      />
                    </div>
                  ))
                )}
              </div>
            </Form.Item>
          )}
        </Form.List>

        <Form.List name='specifications'>
          {(fields, { add, remove }) => (
            <Form.Item required label='规格' className='admin-pkg-form-block'>
              <div className='admin-pkg-form-list'>
                {fields.map((field) => (
                  <div key={field.key} className='admin-benefit-spec-card'>
                    <div className='admin-benefit-spec-main'>
                      <div className='admin-benefit-spec-meta'>
                        <Form.Item
                          name={[field.name, 'quotaLimit']}
                          label='总次数'
                          className='admin-pkg-form-mini'
                          rules={[{ required: true, message: '请输入' }]}
                        >
                          <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder='正整数' />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'validityDays']}
                          label='有效天数'
                          className='admin-pkg-form-mini'
                        >
                          <InputNumber min={1} precision={0} style={{ width: '100%' }} placeholder='空为永久' />
                        </Form.Item>
                      </div>
                      <Form.List name={[field.name, 'prices']}>
                        {(priceFields, { add: addPrice, remove: removePrice }) => (
                          <div className='admin-benefit-spec-prices'>
                            {priceFields.map((priceField) => (
                              <div key={priceField.key} className='admin-benefit-spec-price-line'>
                                <Form.Item
                                  name={[priceField.name, 'price']}
                                  label='价格'
                                  className='admin-pkg-form-mini'
                                >
                                  <InputNumber min={0} style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item
                                  name={[priceField.name, 'currency']}
                                  label='币种'
                                  className='admin-pkg-form-mini'
                                  rules={[{ required: true, message: '请选择' }]}
                                >
                                  <Select options={CURRENCY_OPTIONS} placeholder='币种' style={{ width: '100%' }} />
                                </Form.Item>
                                <Form.Item
                                  name={[priceField.name, 'region']}
                                  label='地区'
                                  className='admin-pkg-form-mini'
                                  rules={[{ required: true, message: '请选择' }]}
                                >
                                  <Select options={REGION_OPTIONS} placeholder='地区' style={{ width: '100%' }} />
                                </Form.Item>
                                <div className='admin-benefit-spec-price-actions'>
                                  <RowActions
                                    plain
                                    onAdd={() => addPrice(emptyPrice())}
                                    onRemove={() => removePrice(priceField.name)}
                                    removeDisabled={priceFields.length <= 1}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </Form.List>
                      <div className='admin-benefit-spec-actions'>
                        <Button type='primary' size='small' icon={<PlusOutlined />} onClick={() => add(emptySpec())} />
                        <Button
                          type='primary'
                          size='small'
                          icon={<MinusOutlined />}
                          disabled={fields.length <= 1}
                          onClick={() => remove(field.name)}
                        />
                      </div>
                    </div>

                  </div>
                ))}
              </div>
            </Form.Item>
          )}
        </Form.List>
      </Form>
    </div>
  );
}
